import type { WorkflowRunStatus } from '../hooks/useWorkflowRuns';
import type { ExecutionEvent } from '../../../components/ExecutionMonitor';

import {
  RUN_PRESTART_MESSAGES,
  type RunPrestartPhase,
  type SandboxV2CopyStage,
} from '@chatsdet/types';

export type LiveRunStatusEvent = {
  message: string;
  phase: RunPrestartPhase | null;
};

/** The short header label for each pre-start phase; the panel text is the shared message. */
const PHASE_LABELS: Record<RunPrestartPhase, string> = {
  waiting_for_snapshot: 'Building snapshot',
  waiting_for_capacity: 'Waiting for capacity',
  restoring_copy: 'Restoring sandbox',
  preparing_copy: 'Preparing sandbox',
  waking_app: 'Starting app',
  signing_in: 'Signing in',
};

const PHASES = new Set<string>(Object.keys(PHASE_LABELS));

function readPhase(value: unknown): RunPrestartPhase | null {
  return typeof value === 'string' && PHASES.has(value) ? (value as RunPrestartPhase) : null;
}

const TERMINAL_STATUS_EVENTS = new Set([
  'success',
  'completed',
  'complete',
  'failed',
  'error',
  'canceled',
  'cancelled',
  'inconclusive',
]);

/** Return the newest useful preparation/status message from the run stream. */
export function getLatestNonTerminalStatusEvent(
  events: ExecutionEvent[]
): LiveRunStatusEvent | null {
  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index];
    if (event.type !== 'status') continue;
    if (
      typeof event.data.status === 'string' &&
      TERMINAL_STATUS_EVENTS.has(event.data.status.toLowerCase())
    )
      continue;
    if (typeof event.data.message !== 'string' || event.data.message.trim().length === 0) continue;
    return {
      message: event.data.message,
      phase: readPhase(event.data.phase),
    };
  }
  return null;
}

/**
 * What the run is doing before its first step: the text for the empty Live
 * View / Agent Thinking panels and a short label for the header (null when
 * there is nothing more specific than the run status to say).
 */
export function getPrestartStatus({
  status,
  events,
  statusEvent,
  sandbox,
}: {
  status: WorkflowRunStatus;
  events: ExecutionEvent[];
  statusEvent: LiveRunStatusEvent | null;
  /** The run's claimed Sandboxes v2 copy, if any. */
  sandbox: { status: string; stage: SandboxV2CopyStage | null } | null;
}): { message: string; label: string | null } {
  if (statusEvent) {
    return {
      message: statusEvent.message,
      label: statusEvent.phase ? PHASE_LABELS[statusEvent.phase] : null,
    };
  }
  // A reloaded page has no events, but the run row still knows the copy's stage.
  // The stored stage outlives the claim; only a still-claiming copy is waiting on it.
  if (sandbox?.status === 'claiming' && sandbox.stage) {
    return { message: RUN_PRESTART_MESSAGES[sandbox.stage], label: PHASE_LABELS[sandbox.stage] };
  }
  // Not queued: the run is past the worker wait, with nothing more specific reported.
  if (status !== 'queued') return { message: 'Preparing the live run…', label: null };
  // The runner emits `{ status: 'running' }` (no message) when a worker claims the run,
  // and a copy only exists once one has.
  const workerClaimed =
    sandbox !== null || events.some((e) => e.type === 'status' && e.data.status === 'running');
  return workerClaimed
    ? { message: 'Starting…', label: null }
    : { message: 'Waiting for a worker…', label: 'Waiting for a worker' };
}
